import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { getFullReports, StatisticsData, PopularBook, RecentActivity } from '../../services/ReportsService';

interface FullStatisticsData extends StatisticsData {
  popularBooks: PopularBook[];
  recentActivity: RecentActivity[];
}

const ReportsPage: React.FC = () => {
  const [statistics, setStatistics] = useState<FullStatisticsData>({
    totalUsers: 0,
    totalBooks: 0,
    totalCollections: 0,
    activeLoans: 0,
    totalReviews: 0,
    popularBooks: [],
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('week');
  
  const navigate = useNavigate();

  useEffect(() => {
    // Verificăm dacă utilizatorul este autentificat și este admin
    const userData = localStorage.getItem('user');
    const authToken = localStorage.getItem('authToken');
    
    if (!userData || !authToken) {
      navigate('/login');
      return;
    }
    
    try {
      const user = JSON.parse(userData);
      
      if (user.role !== 'admin') {
        // Dacă utilizatorul nu este admin, redirecționăm către home
        navigate('/');
        return;
      }
      
      // Încarcă statisticile
      fetchStatistics();
    } catch (err) {
      navigate('/login');
    }
  }, [navigate, timeRange]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        throw new Error('Token de autentificare lipsă');
      }

      const data = await getFullReports(timeRange, authToken);
      
      setStatistics({
        ...data.statistics,
        popularBooks: data.popularBooks,
        recentActivity: data.recentActivity
      });
      
    } catch (err: any) {
      console.error('Eroare la încărcarea datelor:', err);
      
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Token invalid sau expirat, redirecționează la login
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      
      setError(err.message || 'A apărut o eroare la încărcarea statisticilor');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      {/* Spațiu pentru header-ul fix */}
      <div className="pt-24 md:pt-28"></div>
      
      <main className="flex-grow pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Rapoarte și Statistici</h1>
                <p className="mt-1 text-sm text-gray-500">
                  Analizează utilizarea bibliotecii digitale
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <button
                  onClick={() => navigate('/admin/dashboard')}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Înapoi la Dashboard
                </button>
              </div>
            </div>
          </div>

          {/* Afișare eroare */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <p>{error}</p>
              <button 
                onClick={() => setError('')}
                className="float-right"
              >
                &times;
              </button>
            </div>
          )}

          {/* Selectare interval de timp */}
          <div className="bg-white shadow rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <label htmlFor="time-range" className="block text-sm font-medium text-gray-700 mr-4">Interval de timp:</label>
              <select
                id="time-range"
                name="time-range"
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="mt-1 block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="day">Ultimele 24 ore</option>
                <option value="week">Ultima săptămână</option>
                <option value="month">Ultima lună</option>
                <option value="year">Ultimul an</option>
                <option value="all">Tot timpul</option>
              </select>
              <button
                onClick={fetchStatistics}
                className="ml-4 inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
                Actualizează
              </button>
            </div>
          </div>

          {loading ? (
            <div className="bg-white shadow rounded-lg p-12 flex justify-center items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
          {/* Statistici Generale */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5 mb-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Utilizatori</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{statistics.totalUsers.toLocaleString()}</dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Cărți</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{statistics.totalBooks.toLocaleString()}</dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Colecții</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{statistics.totalCollections.toLocaleString()}</dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Activitate Recentă</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{statistics.activeLoans.toLocaleString()}</dd>
              </div>
            </div>
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">Total Recenzii</dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">{statistics.totalReviews.toLocaleString()}</dd>
              </div>
            </div>
          </div>

          {/* Conținut principal */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Cele mai populare cărți */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Cele mai populare cărți
                </h3>
                <p className="mt-1 text-sm text-gray-500">Adăugate la cărți favorite</p>
              </div>
              <div className="px-4 py-5 sm:p-6">
                {statistics.popularBooks.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {statistics.popularBooks.map((book, index) => (
                      <li key={book.id} className="py-3 flex items-center">
                        <span className="w-6 text-center font-medium text-gray-900">{index + 1}.</span>
                        <div className="ml-3 flex-1">
                          <p className="text-sm font-medium text-gray-900">{book.title}</p>
                          <p className="text-sm text-gray-500">{book.author}</p>
                        </div>
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {book.loansCount} {book.loansCount === 1 ? 'utilizator' : 'utilizatori'}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nu există date pentru perioada selectată</p>
                )}
              </div>
            </div>

            {/* Activitate recentă */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Activitate Recentă
                </h3>
                <p className="mt-1 text-sm text-gray-500">Ultimele acțiuni în platformă</p>
              </div>
              <div className="px-4 py-5 sm:p-6">
                {statistics.recentActivity.length > 0 ? (
                  <div className="flow-root">
                    <ul className="-mb-8">
                      {statistics.recentActivity.map((item, itemIdx) => (
                        <li key={item.id}>
                          <div className="relative pb-8">
                            {itemIdx !== statistics.recentActivity.length - 1 ? (
                              <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden="true" />
                            ) : null}
                            <div className="relative flex space-x-3">
                              <div>
                                <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white
                                  ${item.type === 'view' || item.type === 'read' ? 'bg-blue-500' : 
                                    item.type === 'download' ? 'bg-green-500' : 
                                    item.type === 'register' ? 'bg-purple-500' : 
                                    item.type === 'add_book' ? 'bg-yellow-500' : 
                                    item.type === 'review' ? 'bg-pink-500' : 
                                    item.type === 'bookmark' || item.type === 'note' ? 'bg-indigo-500' : 'bg-gray-500'}`}>
                                  <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    {item.type === 'view' || item.type === 'read' || item.type === 'download' ? (
                                      <path fillRule="evenodd" d="M5 4a3 3 0 00-3 3v6a3 3 0 003 3h10a3 3 0 003-3V7a3 3 0 00-3-3H5zm0 2a1 1 0 00-1 1v6a1 1 0 001 1h10a1 1 0 001-1V7a1 1 0 00-1-1H5z" clipRule="evenodd" />
                                    ) : item.type === 'register' ? (
                                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                    ) : item.type === 'add_book' ? (
                                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                    ) : (
                                      <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H6z" clipRule="evenodd" />
                                    )}
                                  </svg>
                                </span>
                              </div>
                              <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                                <div>
                                  <p className="text-sm text-gray-500">
                                    <span className="font-medium text-gray-900">{item.username}</span> {item.actionText}
                                  </p>
                                </div>
                                <div className="text-right text-sm whitespace-nowrap text-gray-500">
                                  {formatDate(item.timestamp)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nu există activitate pentru perioada selectată</p>
                )}
              </div>
            </div>
          </div>
            </>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReportsPage; 
